// API utility with JWT token management
import { API_BASE_URL } from '../config';

const TOKEN_KEY = 'crystalPuzzle_authToken';
const USER_KEY = 'crystalPuzzleUserId';

class ApiClient {
  constructor() {
    this.token = localStorage.getItem(TOKEN_KEY);
    this.onAuthError = null; // callback when token is invalid/expired
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  }

  getToken() {
    return this.token;
  }

  setUserId(userId) {
    if (userId) {
      localStorage.setItem(USER_KEY, userId);
    } else {
      localStorage.removeItem(USER_KEY);
    }
  }

  getUserId() {
    return localStorage.getItem(USER_KEY);
  }

  clearAuth() {
    this.token = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  isAuthenticated() {
    return !!this.token;
  }

  // Set callback for auth errors (e.g., expired token)
  setAuthErrorHandler(handler) {
    this.onAuthError = handler;
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add auth token if available
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Handle 401 - token expired or invalid
      if (response.status === 401) {
        const data = await response.json().catch(() => ({}));
        
        // If this is a login attempt, don't clear auth - just return the error
        if (endpoint === '/login' || endpoint === '/register' || endpoint === '/login-pin') {
          return { response, data };
        }

        // For other requests, the token is invalid
        this.clearAuth();
        if (this.onAuthError) {
          this.onAuthError();
        }
        return { response, data };
      }

      const data = await response.json().catch(() => ({}));
      return { response, data };
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Auth endpoints
  async register({ userId, password, email }) {
    const { response, data } = await this.request('/register', {
      method: 'POST',
      body: JSON.stringify({ userId, password, email }),
    });

    if (response.ok && data.token) {
      this.setToken(data.token);
      this.setUserId(data.userId);
    }

    return { ok: response.ok, status: response.status, data };
  }

  async login({ userId, password }) {
    const { response, data } = await this.request('/login', {
      method: 'POST',
      body: JSON.stringify({ userId, password }),
    });

    if (response.ok && data.token) {
      this.setToken(data.token);
      this.setUserId(data.userId);
    }

    return { ok: response.ok, status: response.status, data };
  }

  async loginWithPin({ userId, pin }) {
    const { response, data } = await this.request('/login-pin', {
      method: 'POST',
      body: JSON.stringify({ userId, pin }),
    });

    if (response.ok && data.token) {
      this.setToken(data.token);
      this.setUserId(data.userId);
    }

    return { ok: response.ok, status: response.status, data };
  }

  async changePassword({ oldPassword, newPassword }) {
    const { response, data } = await this.request('/change-password', {
      method: 'POST',
      body: JSON.stringify({ oldPassword, newPassword }),
    });
    return { ok: response.ok, status: response.status, data };
  }

  async setQuickPin({ password, pin }) {
    const { response, data } = await this.request('/set-quick-pin', {
      method: 'POST',
      body: JSON.stringify({ password, pin }),
    });
    return { ok: response.ok, status: response.status, data };
  }

  async removeQuickPin({ password }) {
    const { response, data } = await this.request('/remove-quick-pin', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
    return { ok: response.ok, status: response.status, data };
  }

  async updateEmail({ password, email }) {
    const { response, data } = await this.request('/update-email', {
      method: 'POST',
      body: JSON.stringify({ password, email }),
    });
    return { ok: response.ok, status: response.status, data };
  }

  async requestPasswordReset({ userId }) {
    const { response, data } = await this.request('/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
    return { ok: response.ok, status: response.status, data };
  }

  async resetPassword({ token, userId, newPassword }) {
    const { response, data } = await this.request('/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, userId, newPassword }),
    });
    return { ok: response.ok, status: response.status, data };
  }

  async adminResetUserPassword({ targetUserId, newPassword, newQuickPin }) {
    const { response, data } = await this.request('/admin/reset-user-password', {
      method: 'POST',
      body: JSON.stringify({ targetUserId, newPassword, newQuickPin }),
    });
    return { ok: response.ok, status: response.status, data };
  }


  async updateThemePreference({ isDarkMode }) {
    const userId = this.getUserId();
    const { response, data } = await this.request('/theme-preference', {
      method: 'POST',
      body: JSON.stringify({ userId, isDarkMode }),
    });
    return { ok: response.ok, status: response.status, data };
  }

  // Logout - just clear local state
  logout() {
    this.clearAuth();
  }
}

// Singleton instance
export const apiClient = new ApiClient();


